import React, { useRef } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Brand assets
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function MiningTable4() {
  const pdfRef = useRef();

  const data = [
    {
      year: 2018,
      female: 870,
      male: 7010,
      female_percent: 11.04,
      male_percent: 88.96,
    },
    {
      year: 2019,
      female: 857,
      male: 6613,
      female_percent: 11.47,
      male_percent: 88.53,
    },
    {
      year: 2020,
      female: 910,
      male: 6499,
      female_percent: 12.28,
      male_percent: 87.72,
    },
    {
      year: 2021,
      female: 958,
      male: 6951,
      female_percent: 12.11,
      male_percent: 87.89,
    },
    {
      year: 2022,
      female: 1104,
      male: 7519,
      female_percent: 12.8,
      male_percent: 87.2,
    },
    {
      year: 2023,
      female: 1238,
      male: 7799,
      female_percent: 13.7,
      male_percent: 86.3,
    },
    {
      year: 2024,
      female: 1324,
      male: 8102,
      female_percent: 14.05,
      male_percent: 85.95,
    },
  ];

  // Bar Chart (Absolute Numbers)
  const barData = {
    labels: data.map((d) => d.year),
    datasets: [
      {
        label: "Female Employment",
        data: data.map((d) => d.female),
        backgroundColor: "rgba(236, 72, 153, 0.85)",
        borderRadius: 6,
      },
      {
        label: "Male Employment",
        data: data.map((d) => d.male),
        backgroundColor: "rgba(14, 165, 233, 0.85)",
        borderRadius: 6,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "⛏️ Utah Mining Employment by Gender (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" }, grid: { display: false } },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Number of Employees" },
      },
    },
  };

  // Line Chart (Percentages)
  const lineData = {
    labels: data.map((d) => d.year),
    datasets: [
      {
        label: "Female Share (%)",
        data: data.map((d) => d.female_percent),
        borderColor: "rgba(236, 72, 153, 1)",
        backgroundColor: "rgba(236, 72, 153, 0.2)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Male Share (%)",
        data: data.map((d) => d.male_percent),
        borderColor: "rgba(14, 165, 233, 1)",
        backgroundColor: "rgba(14, 165, 233, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Gender Employment Share (%) – Utah Mining (2018–2024)",
        font: { size: 15, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Share (%)" },
      },
    },
  };

  // Summary insights
  const start = data[0];
  const end = data[data.length - 1];
  const femaleGrowth = (
    ((end.female - start.female) / start.female) *
    100
  ).toFixed(2);
  const femaleShareChange = (end.female_percent - start.female_percent).toFixed(
    2
  );
  const peakYear = data.reduce((max, d) => (d.female > max.female ? d : max));

  // PDF Export
  const downloadPDF = async () => {
    const input = pdfRef.current;
    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
    pdf.setFontSize(26);
    pdf.text("UTahData.org", 95, 22);
    pdf.line(10, 35, 200, 35);
    pdf.addImage(imgData, "PNG", 5, 38, imgWidth, imgHeight);

    const footerY = 275;
    pdf.line(10, footerY - 5, 200, footerY - 5);
    pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
    pdf.setFontSize(10);
    pdf.text("Built by", 55, footerY + 10);
    pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
    pdf.text("using", 112, footerY + 10);
    pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
    pdf.text("20250916.v3.198", 148, footerY + 10);
    pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 10, 295);
    pdf.text(`From: ${window.location.href}`, 100, 295);

    pdf.save("utah_mining_gender_share.pdf");
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-pink-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Utah Mining Employment Share (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Female Employment Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-blue-800">+{femaleGrowth}%</p>
          </div>
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Female Share Increase
            </p>
            <p className="text-xl font-bold text-pink-800">
              +{femaleShareChange}%
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Female Year
            </p>
            <p className="text-xl font-bold text-yellow-800">{peakYear.year}</p>
          </div>
        </div>

        {/* Table + Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          {/* <div className="flex flex-col h-full"> */}
            <div className="overflow-x-auto flex-grow">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    <th className="p-2">Female</th>
                    <th className="p-2">Male</th>
                    <th className="p-2">Female %</th>
                    <th className="p-2">Male %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${
                        row.year === 2024
                          ? "bg-green-100 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2">{row.year}</td>
                      <td className="p-2 text-pink-700">
                        {row.female.toLocaleString()}
                      </td>
                      <td className="p-2 text-blue-700">
                        {row.male.toLocaleString()}
                      </td>
                      <td className="p-2 text-pink-600">
                        {row.female_percent}%
                      </td>
                      <td className="p-2 text-blue-600">{row.male_percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          {/* </div> */}

          {/* Chart 1 (Absolute Numbers) */}
          <div className="flex flex-col h-full">
            <div className="bg-white rounded-lg shadow-md p-4 flex-grow">
              <Bar data={barData} options={barOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
